import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * useCellSelection — seleção de células estilo Excel na DataTable de /operacoes.
 *
 * Interação (inspirada no produto.php do hostgator, planos_new):
 *   - Click + Arrastar sobre células NUMÉRICAS = seleção RETANGULAR (anchor →
 *     focus). Clique simples (sem arrasto) NÃO seleciona — preserva o clique
 *     normal da row que abre o DetailPanel.
 *   - Barra flutuante mostra Contagem / Soma / Média / Mín / Máx das células
 *     numéricas dentro do retângulo.
 *   - Esc ou clique fora limpa. Reposiciona a barra no scroll/resize.
 *
 * Os eventos são DELEGADOS no container (um mousedown/mouseover só) — em vez de
 * handlers por célula (seriam milhares com page=500). Cada célula numérica
 * carrega `data-sel-rc="<ri>:<ci>"`; o hook lê isso do alvo do evento.
 *
 * Geometria por índices (ri = índice da row na PÁGINA, ci = índice da coluna).
 * Os VALORES vêm de callbacks do caller (getCellNumber/isColNumeric) — o hook
 * não conhece o shape das rows nem o pipeline.
 *
 * @param {{
 *   containerRef: { current: HTMLElement | null },
 *   isColNumeric: (ci:number)=>boolean,
 *   getCellNumber: (ri:number, ci:number)=>number|null,
 * }} params
 */
export function useCellSelection({ containerRef, isColNumeric, getCellNumber }) {
    const [sel, setSel] = useState(null); // { aR, aC, fR, fC } | null
    const [selecting, setSelecting] = useState(false); // arrasto ativo (p/ user-select:none)
    const [barPos, setBarPos] = useState(null); // { left, top } em coords fixed

    const anchorRef = useRef(null);
    const draggingRef = useRef(false);
    const movedRef = useRef(false);
    const suppressClickRef = useRef(false);

    const clear = useCallback(() => {
        draggingRef.current = false;
        movedRef.current = false;
        anchorRef.current = null;
        setSel(null);
        setSelecting(false);
        setBarPos(null);
    }, []);

    // --- Delegação dos eventos de arrasto no container -----------------------
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        const cellFromEvent = (e) => {
            const el = e.target.closest?.('[data-sel-rc]');
            if (!el || !container.contains(el)) return null;
            const raw = el.getAttribute('data-sel-rc');
            const [r, c] = raw.split(':').map(Number);
            if (!Number.isFinite(r) || !Number.isFinite(c)) return null;
            return { r, c };
        };

        const onDown = (e) => {
            if (e.button !== 0) return; // só botão esquerdo
            const cell = cellFromEvent(e);
            if (!cell) return; // mousedown fora de célula numérica → ignora (deixa navegar)
            draggingRef.current = true;
            movedRef.current = false;
            anchorRef.current = { r: cell.r, c: cell.c };
            setSel({ aR: cell.r, aC: cell.c, fR: cell.r, fC: cell.c });
            setSelecting(false);
            // NÃO previne default aqui — clique simples ainda precisa disparar
            // o onClick da row (navegação). O preventDefault vem no arrasto.
        };

        const onOver = (e) => {
            if (!draggingRef.current) return;
            const cell = cellFromEvent(e);
            if (!cell) return;
            const a = anchorRef.current;
            if (!a) return;
            if (cell.r !== a.r || cell.c !== a.c) {
                movedRef.current = true;
                setSelecting(true);
                e.preventDefault(); // mata a seleção de texto nativa durante o arrasto
            }
            setSel((prev) =>
                prev && prev.fR === cell.r && prev.fC === cell.c
                    ? prev
                    : { aR: a.r, aC: a.c, fR: cell.r, fC: cell.c },
            );
        };

        container.addEventListener('mousedown', onDown);
        container.addEventListener('mouseover', onOver);
        return () => {
            container.removeEventListener('mousedown', onDown);
            container.removeEventListener('mouseover', onOver);
        };
    }, [containerRef]);

    // --- Fim do arrasto (mouseup global) ------------------------------------
    useEffect(() => {
        const onUp = () => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            setSelecting(false);
            if (movedRef.current) {
                // Houve arrasto real → engole o `click` que a row vai disparar
                // logo após o mouseup (senão abriria o DetailPanel). O flag é
                // consumido pelo onClick da row; auto-reseta no próximo tick
                // caso nenhum click venha (ex: mouseup fora de qualquer row).
                suppressClickRef.current = true;
                setTimeout(() => {
                    suppressClickRef.current = false;
                }, 0);
            } else {
                // Clique simples (sem arrasto) → sem seleção; deixa a navegação.
                anchorRef.current = null;
                setSel(null);
                setBarPos(null);
            }
        };
        document.addEventListener('mouseup', onUp);
        return () => document.removeEventListener('mouseup', onUp);
    }, []);

    const consumeClickSuppression = useCallback(() => {
        if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return true;
        }
        return false;
    }, []);

    // --- Retângulo + teste de pertencimento ---------------------------------
    const rect = useMemo(() => {
        if (!sel) return null;
        return {
            rMin: Math.min(sel.aR, sel.fR),
            rMax: Math.max(sel.aR, sel.fR),
            cMin: Math.min(sel.aC, sel.fC),
            cMax: Math.max(sel.aC, sel.fC),
        };
    }, [sel]);

    // Só conta como "seleção visível" quando cobre mais de uma célula (evita
    // barra piscando no mousedown de um clique que pode virar navegação).
    const spansMultiple = !!rect && (rect.rMax > rect.rMin || rect.cMax > rect.cMin);

    const isSelected = useCallback(
        (ri, ci) => {
            if (!rect || !spansMultiple) return false;
            return (
                ri >= rect.rMin &&
                ri <= rect.rMax &&
                ci >= rect.cMin &&
                ci <= rect.cMax &&
                isColNumeric(ci)
            );
        },
        [rect, spansMultiple, isColNumeric],
    );

    // --- Estatísticas (só células numéricas dentro do retângulo) ------------
    const stats = useMemo(() => {
        if (!rect || !spansMultiple) return null;
        let count = 0;
        let sum = 0;
        let min = Infinity;
        let max = -Infinity;
        for (let ci = rect.cMin; ci <= rect.cMax; ci += 1) {
            if (!isColNumeric(ci)) continue;
            for (let ri = rect.rMin; ri <= rect.rMax; ri += 1) {
                const v = getCellNumber(ri, ci);
                if (v === null || v === undefined || Number.isNaN(v)) continue;
                count += 1;
                sum += v;
                if (v < min) min = v;
                if (v > max) max = v;
            }
        }
        if (count === 0) return null;
        return { count, sum, avg: sum / count, min, max };
    }, [rect, spansMultiple, isColNumeric, getCellNumber]);

    // --- Posição da barra (centro acima do retângulo) -----------------------
    const recomputeBarPos = useCallback(() => {
        const container = containerRef.current;
        if (!container || !sel || !spansMultiple) return;
        const a = container.querySelector(`[data-sel-rc="${sel.aR}:${sel.aC}"]`);
        const f = container.querySelector(`[data-sel-rc="${sel.fR}:${sel.fC}"]`);
        if (!a || !f) return;
        const ra = a.getBoundingClientRect();
        const rf = f.getBoundingClientRect();
        const left = (Math.min(ra.left, rf.left) + Math.max(ra.right, rf.right)) / 2;
        const top = Math.min(ra.top, rf.top);
        // Clampa pra barra não sumir acima do topo da viewport.
        setBarPos({ left, top: Math.max(top, 44) });
    }, [containerRef, sel, spansMultiple]);

    useLayoutEffect(() => {
        recomputeBarPos();
    }, [recomputeBarPos]);

    useEffect(() => {
        if (!sel || !spansMultiple) return undefined;
        const onScrollOrResize = () => recomputeBarPos();
        // capture=true pega o scroll do container interno (não só window).
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [sel, spansMultiple, recomputeBarPos]);

    // --- Esc + clique fora limpa --------------------------------------------
    useEffect(() => {
        if (!sel) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') clear();
        };
        const onDown = (e) => {
            const container = containerRef.current;
            if (container && container.contains(e.target)) return; // dentro da tabela = nova seleção
            if (e.target.closest?.('[data-sel-bar="1"]')) return; // na própria barra
            clear();
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onDown, true);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onDown, true);
        };
    }, [sel, clear, containerRef]);

    return {
        isSelected,
        stats,
        selecting,
        barVisible: spansMultiple && !!stats,
        barPos,
        consumeClickSuppression,
        clear,
    };
}
